import { ref } from 'vue';
import { useCustomerStore } from '@/stores/customer.store';
import type { Customer } from '@/_helpers/interfaces/customers/customer.interface';
import { debounce } from 'lodash';

interface UseCustomerSelectOptions {
  pageSize?: number;
  debounceMs?: number;
}

/**
 * Server-side searchable + paginated list of active customers, for use with
 * SearchableSelect (remote-search mode). Mirrors useProjectSelect, but the
 * customers endpoint paginates with `next` (a URL) rather than a page number,
 * so we track the page counter ourselves.
 */
export function useCustomerSelect(options: UseCustomerSelectOptions = {}) {
  const { pageSize = 50, debounceMs = 300 } = options;
  const { getCustomersList } = useCustomerStore();

  const customers = ref<Customer[]>([]);
  const loading = ref(false);
  const hasMore = ref(false);
  const page = ref(1);
  const lastSearch = ref('');

  async function fetchCustomers(search?: string) {
    loading.value = true;
    lastSearch.value = search || '';
    page.value = 1;
    try {
      const response = await getCustomersList({
        page_size: pageSize,
        page: 1,
        is_active: true,
        search: search || undefined,
      });
      customers.value = [...response.results];
      hasMore.value = !!response.next;
    } finally {
      loading.value = false;
    }
  }

  async function loadMore() {
    if (!hasMore.value || loading.value) return;
    loading.value = true;
    page.value += 1;
    try {
      const response = await getCustomersList({
        page_size: pageSize,
        page: page.value,
        is_active: true,
        search: lastSearch.value || undefined,
      });
      customers.value = [...customers.value, ...response.results];
      hasMore.value = !!response.next;
    } finally {
      loading.value = false;
    }
  }

  const onSearch = debounce((query: string) => {
    fetchCustomers(query);
  }, debounceMs);

  return { customers, loading, hasMore, fetchCustomers, loadMore, onSearch };
}
